import { useProfessional, useSession } from '@lindeza/core/hooks';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Dock, Rail, Toasts } from '../components/ui';
import { Agenda } from '../screens/agenda/Agenda';
import { Clientes } from '../screens/clientes/Clientes';
import { Cadastro } from '../screens/entrar/Cadastro';
import { CodigoPorEmail, VerifyLink } from '../screens/entrar/CodigoPorEmail';
import { Login } from '../screens/entrar/Login';
import { Welcome } from '../screens/entrar/Welcome';
import { UpdateBanner } from './Banners';
import { DESKTOP, useMediaQuery } from './useMediaQuery';
import { useSyncLoop } from './useSyncLoop';

// Telas menos usadas carregam sob demanda (o service worker já guarda todas para uso offline).
const Ajustes = lazy(() => import('../screens/ajustes/Ajustes').then((m) => ({ default: m.Ajustes })));
const Mais = lazy(() => import('../screens/ajustes/Ajustes').then((m) => ({ default: m.Mais })));
const Caixa = lazy(() => import('../screens/caixa/Caixa').then((m) => ({ default: m.Caixa })));
const ClientDetailScreen = lazy(() => import('../screens/clientes/ClientDetail').then((m) => ({ default: m.ClientDetailScreen })));
const Divulgar = lazy(() => import('../screens/divulgar/Divulgar').then((m) => ({ default: m.Divulgar })));
const Estoque = lazy(() => import('../screens/estoque/Estoque').then((m) => ({ default: m.Estoque })));
const OpenWeek = lazy(() => import('../screens/divulgar/OpenWeek').then((m) => ({ default: m.OpenWeek })));
const Onboarding = lazy(() => import('../screens/onboarding/Onboarding').then((m) => ({ default: m.Onboarding })));

export function App() {
  const ready = useSession((s) => s.ready);
  const mode = useSession((s) => s.mode);
  const token = useSession((s) => s.token);
  const professional = useProfessional();
  const location = useLocation();
  useSyncLoop();
  // Só um dos dois existe na tela: o trilho no computador, a doca no celular e no tablet.
  const desktop = useMediaQuery(DESKTOP);

  if (!ready) return <Boot />;

  // Sem sessão: entrar ou experimentar.
  if (mode === 'none') {
    return (
      <>
        <Routes>
          <Route path="/entrar" element={<Login />} />
          <Route path="/entrar/codigo" element={<CodigoPorEmail />} />
          <Route path="/cadastro" element={<Cadastro />} />
          <Route path="/entrar/verificar" element={<VerifyLink />} />
          <Route path="*" element={<Welcome />} />
        </Routes>
        <Toasts />
      </>
    );
  }

  // Sessão expirada (token revogado): pede o código de novo, sem perder os dados.
  if (mode === 'account' && !token && location.pathname !== '/entrar/verificar') {
    return (
      <>
        <CodigoPorEmail expired />
        <Toasts />
      </>
    );
  }

  if (!professional) return <Boot />;

  if (!professional.onboarded_at && location.pathname !== '/entrar/verificar') {
    return (
      <>
        <Suspense fallback={null}>
          <Onboarding />
        </Suspense>
        <Toasts />
      </>
    );
  }

  return (
    <div className="app">
      {desktop && <Rail />}
      <div className="app-main">
        <UpdateBanner />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Agenda />} />
            <Route path="/agenda/:date" element={<Agenda />} />
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/clientes/:id" element={<ClientDetailScreen />} />
            <Route path="/caixa" element={<Caixa />} />
            <Route path="/estoque" element={<Estoque />} />
            <Route path="/estoque/:id" element={<Estoque />} />
            <Route path="/divulgar" element={<Divulgar />} />
            <Route path="/divulgar/abrir" element={<OpenWeek />} />
            <Route path="/ajustes/*" element={<Ajustes />} />
            <Route path="/mais" element={<Mais />} />
            <Route path="/entrar/verificar" element={<VerifyLink />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {!desktop && <Dock />}
      <Toasts />
    </div>
  );
}

/** Abrindo: até 300 ms só a data; depois o esqueleto da agenda e "Abrindo sua agenda" (sem brilho animado). */
function Boot() {
  const [late, setLate] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setLate(true), 300);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="boot" data-late={late ? '1' : '0'} aria-busy="true">
      <div className="boot-date" />
      <div className="boot-rows" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="boot-row">
            <i />
            <i />
            <i />
          </div>
        ))}
      </div>
      <p className="boot-msg" role="status">
        Abrindo sua agenda
      </p>
    </div>
  );
}
